import { Injectable } from '@angular/core';
import { distinctUntilChanged, interval, map, merge, Observable, startWith, Subject } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class AuthRateLimitService {
  private readonly storageKey = 'ie-pos:auth-rate-limit';
  private readonly lockouts = new Map<string, Date>();
  private readonly attempts = new Map<string, number[]>();
  private readonly lockoutChanges$ = new Subject<void>();

  constructor() {
    this.restoreState();
  }

  setLockout(routeKey: string, seconds: number): void {
    const until = new Date(Date.now() + seconds * 1000);
    this.lockouts.set(routeKey, until);
    this.attempts.delete(routeKey);
    this.persistState();
    this.lockoutChanges$.next();
  }

  recordAttempt(
    routeKey: string,
    options: { maxAttempts: number; windowSeconds: number },
  ): boolean {
    const now = Date.now();
    const windowStart = now - options.windowSeconds * 1000;
    const recentAttempts = (this.attempts.get(routeKey) ?? [])
      .filter((attemptAt) => attemptAt >= windowStart);

    recentAttempts.push(now);
    this.attempts.set(routeKey, recentAttempts.slice(-options.maxAttempts));
    this.persistState();

    return recentAttempts.length >= options.maxAttempts;
  }

  isLockedOut(routeKey: string): boolean {
    const until = this.lockouts.get(routeKey);
    if (!until) {
      return false;
    }
    if (Date.now() >= until.getTime()) {
      this.lockouts.delete(routeKey);
      this.persistState();
      return false;
    }
    return true;
  }

  getRemainingSeconds(routeKey: string): number {
    const until = this.lockouts.get(routeKey);
    if (!until) {
      return 0;
    }
    return Math.max(0, Math.ceil((until.getTime() - Date.now()) / 1000));
  }

  remainingSeconds$(routeKey: string): Observable<number> {
    return merge(interval(1000), this.lockoutChanges$).pipe(
      startWith(0),
      map(() => this.getRemainingSeconds(routeKey)),
      distinctUntilChanged(),
    );
  }

  reset(routeKey: string): void {
    this.lockouts.delete(routeKey);
    this.attempts.delete(routeKey);
    this.persistState();
    this.lockoutChanges$.next();
  }

  private restoreState(): void {
    try {
      const rawState = localStorage.getItem(this.storageKey);
      if (!rawState) {
        return;
      }

      const state = JSON.parse(rawState) as {
        lockouts?: Record<string, string>;
        attempts?: Record<string, number[]>;
      };
      const now = Date.now();

      Object.entries(state.lockouts ?? {}).forEach(([key, value]) => {
        const until = new Date(value);
        if (Number.isFinite(until.getTime()) && until.getTime() > now) {
          this.lockouts.set(key, until);
        }
      });

      Object.entries(state.attempts ?? {}).forEach(([key, values]) => {
        const validAttempts = values.filter((attemptAt) => Number.isFinite(attemptAt));
        if (validAttempts.length > 0) {
          this.attempts.set(key, validAttempts);
        }
      });

      this.persistState();
    } catch {
      localStorage.removeItem(this.storageKey);
    }
  }

  private persistState(): void {
    try {
      const state = {
        lockouts: Object.fromEntries(
          Array.from(this.lockouts.entries()).map(([key, value]) => [key, value.toISOString()]),
        ),
        attempts: Object.fromEntries(this.attempts.entries()),
      };

      if (
        Object.keys(state.lockouts).length === 0 &&
        Object.keys(state.attempts).length === 0
      ) {
        localStorage.removeItem(this.storageKey);
        return;
      }

      localStorage.setItem(this.storageKey, JSON.stringify(state));
    } catch {
      // Si el navegador bloquea storage, el rate-limit en memoria sigue funcionando.
    }
  }
}
